import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ResponsiveContainer, ComposedChart, BarChart, AreaChart, Bar, Line, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import Icon from './icons';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// One palette for every chart. Each colour differs in lightness as well as hue,
// so the series stay apart for colour-blind readers and in dark mode.
const C = {
    pro: '#E8861A',
    k12: '#2F6FDF',
    api: '#7B5CE0',
    money: '#0F9D76',
    alt: '#C2410C',
    grey: '#94A3B8',
    bad: '#DC2626'
};

const nf = new Intl.NumberFormat('en-US');
const num = v => nf.format(Math.round(Number(v) || 0));
const usd = v => '$' + nf.format(Math.round(Number(v) || 0));
const usd2 = v => '$' + (Number(v) || 0).toFixed(2);
const short = v => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (Math.abs(n) >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k';
    return String(Math.round(n));
};
const dayLabel = d => {
    const [, m, dd] = d.split('-');
    return `${dd} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m) - 1]}`;
};

function useIsDark() {
    const [dark, setDark] = useState(() => document.documentElement.dataset.theme === 'dark');
    useEffect(() => {
        const el = document.documentElement;
        const o = new MutationObserver(() => setDark(el.dataset.theme === 'dark'));
        o.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
        return () => o.disconnect();
    }, []);
    return dark;
}

/** Card shell so every panel on the page lines up. */
function Panel({ title, hint, right, children, pad = true }) {
    return (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: pad ? undefined : 0 }}>
            {(title || right) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        {title && <h3 className="card-title" style={{ margin: 0 }}>{title}</h3>}
                        {hint && <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--app-muted)' }}>{hint}</p>}
                    </div>
                    {right}
                </div>
            )}
            {children}
        </div>
    );
}

function Kpi({ label, value, sub, tone }) {
    return (
        <div className="stat-card" style={tone ? { borderLeftColor: tone } : undefined}>
            <div className="stat-label">{label}</div>
            <div className="stat-value">{value}</div>
            {sub && <div style={{ fontSize: '12px', color: 'var(--app-muted)', marginTop: '2px' }}>{sub}</div>}
        </div>
    );
}

function AdminDashboard() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    // Which activation tier the Activations panel shows. K12 (free school
    // bulk-adds) dwarf paid Pro activations, so the whole page defaults to Pro.
    const [actMetric, setActMetric] = useState('pro');
    const dark = useIsDark();

    const axis = dark ? '#8A93A5' : '#64748B';
    const grid = dark ? '#2A3040' : '#E8ECF3';
    const tipStyle = {
        background: dark ? '#1C2230' : '#FFFFFF',
        border: `1px solid ${dark ? '#333B4D' : '#E3E6EE'}`,
        borderRadius: 10,
        fontSize: 13,
        color: dark ? '#E7EAF0' : '#151821'
    };

    const load = useCallback(async (d) => {
        setLoading(true); setErr(null);
        try {
            const res = await fetch(`${API_URL}/admin/dashboard?days=${d}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
            });
            const body = await res.json();
            if (!res.ok || body.error) setErr(body.error || 'Could not load the dashboard');
            else setData(body);
        } catch (e) {
            setErr('Connection error. Please try again.');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(days); }, [load, days]);

    const refresh = async () => {
        setRefreshing(true);
        try {
            await fetch(`${API_URL}/admin/dashboard/rollup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
                body: JSON.stringify({ days: 3 })
            });
            await load(days);
        } catch (e) { setErr('Could not refresh'); }
        setRefreshing(false);
    };

    // Everything the charts need, derived once per load.
    const view = useMemo(() => {
        if (!data) return null;
        const s = (data.series || []).map(r => ({
            ...r,
            label: dayLabel(r.day),
            act_total: r.act_total || 0, act_k12: r.act_k12 || 0, act_pro: r.act_pro || 0, act_api: r.act_api || 0,
            bot_orders: r.bot_orders || 0, bot_usd: r.bot_usd || 0, bot_refunded: r.bot_refunded || 0,
            dep_binance: r.dep_binance || 0, dep_crypto: r.dep_crypto || 0, dep_admin: r.dep_admin || 0, dep_other: r.dep_other || 0,
            mkt_orders: r.mkt_orders || 0, mkt_gc: r.mkt_gc || 0, mkt_refunded: r.mkt_refunded || 0,
            trf_transfer: r.trf_transfer || 0, trf_org: r.trf_org || 0, trf_switch: r.trf_switch || 0, trf_email: r.trf_email || 0,
            api_calls: r.api_calls || 0, api_credits: r.api_calls_credits || 0,
            health_dead: r.health_dead || 0, health_ratelimit: r.health_ratelimit || 0
        }));
        // 7-day average line on activations, so spikes read as spikes. One per
        // tier so the trend line follows whichever view is selected.
        s.forEach((r, i) => {
            const w = s.slice(Math.max(0, i - 6), i + 1);
            const avg = k => Math.round(w.reduce((a, x) => a + (x[k] || 0), 0) / w.length);
            r.act_avg = avg('act_total');
            r.act_avg_pro = avg('act_pro');
            r.act_avg_k12 = avg('act_k12');
            r.dep_total = r.dep_binance + r.dep_crypto + r.dep_admin + r.dep_other;
            r.trf_total = r.trf_transfer + r.trf_org + r.trf_switch + r.trf_email;
        });

        // K12 (free school bulk-adds) dominate the raw total, so Pro is the
        // default and the weekly/monthly roll-ups follow the same choice.
        const metricKey = actMetric === 'all' ? 'act_total' : actMetric === 'k12' ? 'act_k12' : 'act_pro';

        const sum = k => s.reduce((a, r) => a + (r[k] || 0), 0);
        const today = s[s.length - 1] || {};

        // Weekly buckets (Mon-start) from the same daily rows.
        const weeks = {};
        for (const r of s) {
            const dt = new Date(r.day + 'T00:00:00Z');
            const monday = new Date(dt.getTime() - ((dt.getUTCDay() + 6) % 7) * 864e5).toISOString().slice(0, 10);
            weeks[monday] = (weeks[monday] || 0) + (r[metricKey] || 0);
        }
        const weekly = Object.entries(weeks).map(([w, v]) => ({ label: dayLabel(w), value: v }));

        const months = {};
        for (const r of s) months[r.day.slice(0, 7)] = (months[r.day.slice(0, 7)] || 0) + (r[metricKey] || 0);
        const monthly = Object.entries(months).map(([m, v]) => ({ label: m, value: v }));

        const actTotal = sum('act_total'), k12Total = sum('act_k12');
        const botCostRev = sum('bot_cost_rev'), botCost = sum('bot_cost'), botCostOrders = sum('bot_cost_orders');

        return {
            s, weekly, monthly, today, metricKey,
            totals: {
                act: actTotal, k12: k12Total, pro: actTotal - k12Total, api: sum('act_api'),
                apiCalls: sum('api_calls'), apiCredits: sum('api_credits'),
                botOrders: sum('bot_orders'), botUsd: sum('bot_usd'), botRefunded: sum('bot_refunded'),
                dep: sum('dep_binance') + sum('dep_crypto') + sum('dep_admin') + sum('dep_other'),
                depBinance: sum('dep_binance'), depCrypto: sum('dep_crypto'),
                mktOrders: sum('mkt_orders'), mktGc: sum('mkt_gc'), mktRefunded: sum('mkt_refunded'),
                trf: sum('trf_transfer') + sum('trf_org') + sum('trf_switch') + sum('trf_email'),
                trfTransfer: sum('trf_transfer'), trfOrg: sum('trf_org'), trfSwitch: sum('trf_switch'), trfEmail: sum('trf_email'),
                incidents: sum('health_dead') + sum('health_ratelimit'),
                topup: sum('topup_credits')
            },
            profit: {
                orders: botCostOrders, revenue: botCostRev, cost: botCost,
                margin: botCostRev - botCost,
                pct: botCostRev > 0 ? Math.round((botCostRev - botCost) / botCostRev * 100) : 0
            }
        };
    }, [data, actMetric]);

    const rangeBtn = (d, label) => (
        <button key={d} type="button" onClick={() => setDays(d)}
            className={`btn ${days === d ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '6px 14px', fontSize: '13px' }}>{label}</button>
    );

    if (loading && !data) return <div className="loading">Loading dashboard…</div>;
    if (err && !data) return <div className="alert alert-error"><span><Icon name="alert" size={16} /></span><span>{err}</span></div>;
    if (!view) return null;

    const t = view.totals;
    const avgKey = actMetric === 'all' ? 'act_avg' : actMetric === 'k12' ? 'act_avg_k12' : 'act_avg_pro';
    const metricWord = actMetric === 'all' ? '' : actMetric === 'k12' ? 'K12 ' : 'Pro ';
    const metricBtn = (m, label) => (
        <button key={m} type="button" onClick={() => setActMetric(m)}
            className={`btn ${actMetric === m ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '4px 10px', fontSize: '12px' }}>{label}</button>
    );
    const teams = data.teams || {};
    const tierData = [
        { name: 'Activations', Pro: t.pro, K12: t.k12 },
        { name: 'Active users', Pro: (data.activeUsers || 0) - (data.activeUsersK12 || 0), K12: data.activeUsersK12 || 0 },
        { name: 'Teams in use', Pro: (teams.withUsers || 0) - (teams.k12WithUsers || 0), K12: teams.k12WithUsers || 0 }
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {err && <div className="alert alert-error"><span><Icon name="alert" size={16} /></span><span>{err}</span></div>}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--app-muted)' }}>
                    Showing the last {days} days · figures refreshed every 15 minutes
                </p>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {rangeBtn(7, '7 days')}{rangeBtn(30, '30 days')}{rangeBtn(90, '90 days')}
                    <button type="button" className="btn btn-secondary" onClick={refresh} disabled={refreshing}
                        style={{ padding: '6px 14px', fontSize: '13px' }}>{refreshing ? 'Refreshing…' : '↻ Refresh'}</button>
                </div>
            </div>

            {/* Headline numbers */}
            <div className="stats-grid">
                <Kpi label="Pro activations today" value={num(view.today.act_pro)} sub={`${num(t.pro)} Pro · ${num(t.k12)} K12 in ${days} days`} tone={C.pro} />
                <Kpi label="Active users now" value={num(data.activeUsers)} sub={`${num(data.activeUsersK12)} in K12 teams`} tone={C.k12} />
                <Kpi label="Teams in use" value={num(teams.withUsers)} sub={`of ${num(teams.total)} · ${num(teams.k12WithUsers)} K12`} tone={C.api} />
                <Kpi label="Bot revenue" value={usd(t.botUsd)} sub={`${num(t.botOrders)} orders · ${num(t.botRefunded)} refunded`} tone={C.money} />
                <Kpi label="Deposits" value={usd(t.dep)} sub={`Binance ${usd(t.depBinance)} · crypto ${usd(t.depCrypto)}`} tone={C.money} />
                <Kpi label="Marketplace" value={`${num(t.mktGc)} GC`} sub={`${num(t.mktOrders)} orders · ${num(data.top?.marketplaceBuyers)} resellers`} tone={C.alt} />
                <Kpi label="API activations" value={num(t.api)} sub={`${num(data.apiKeys?.active)} active keys`} tone={C.api} />
                <Kpi label="Teams needing attention" value={num((teams.withError || 0) + (teams.rateLimitedNow || 0))}
                    sub={`${num(teams.withError)} with errors · ${num(teams.rateLimitedNow)} rate-limited`}
                    tone={(teams.withError || teams.rateLimitedNow) ? C.bad : C.grey} />
            </div>

            {/* Activations */}
            <Panel title="Adobe activations per day"
                hint="New users added per day · renewals, reinvites and extensions are not counted"
                right={<div style={{ display: 'flex', gap: '6px' }}>{metricBtn('pro', 'Pro')}{metricBtn('all', 'All')}{metricBtn('k12', 'K12')}</div>}>
                <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={view.s} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke={grid} vertical={false} />
                        <XAxis dataKey="label" stroke={axis} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
                        <YAxis stroke={axis} tick={{ fontSize: 11 }} tickFormatter={short} width={48} />
                        <Tooltip contentStyle={tipStyle} formatter={(v, n) => [num(v), n]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {actMetric !== 'k12' && <Bar dataKey="act_pro" name="Pro" stackId="a" fill={C.pro} radius={actMetric === 'all' ? [0, 0, 0, 0] : [3, 3, 0, 0]} />}
                        {actMetric !== 'pro' && <Bar dataKey="act_k12" name="K12" stackId="a" fill={C.k12} radius={[3, 3, 0, 0]} />}
                        <Line dataKey={avgKey} name="7-day average" stroke={dark ? '#E7EAF0' : '#151821'} strokeWidth={2} dot={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            </Panel>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 420px', minWidth: 0 }}>
                    <Panel title={`Weekly ${metricWord}activations`} hint="Mondays">
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={view.weekly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke={grid} vertical={false} />
                                <XAxis dataKey="label" stroke={axis} tick={{ fontSize: 11 }} />
                                <YAxis stroke={axis} tick={{ fontSize: 11 }} tickFormatter={short} width={48} />
                                <Tooltip contentStyle={tipStyle} formatter={v => [num(v), 'Activations']} />
                                <Bar dataKey="value" fill={C.pro} radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Panel>
                </div>
                <div style={{ flex: '1 1 420px', minWidth: 0 }}>
                    <Panel title={`Monthly ${metricWord}activations`} hint="Totals inside the selected range">
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={view.monthly} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                                <CartesianGrid stroke={grid} horizontal={false} />
                                <XAxis type="number" stroke={axis} tick={{ fontSize: 11 }} tickFormatter={short} />
                                <YAxis type="category" dataKey="label" stroke={axis} tick={{ fontSize: 11 }} width={60} />
                                <Tooltip contentStyle={tipStyle} formatter={v => [num(v), 'Activations']} />
                                <Bar dataKey="value" fill={C.alt} radius={[0, 3, 3, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Panel>
                </div>
            </div>

            {/* K12 vs Pro + API */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 420px', minWidth: 0 }}>
                    <Panel title="K12 vs Pro" hint="Split by the team each user is in now">
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={tierData} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                                <CartesianGrid stroke={grid} horizontal={false} />
                                <XAxis type="number" stroke={axis} tick={{ fontSize: 11 }} tickFormatter={short} />
                                <YAxis type="category" dataKey="name" stroke={axis} tick={{ fontSize: 11 }} width={92} />
                                <Tooltip contentStyle={tipStyle} formatter={v => num(v)} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="Pro" stackId="t" fill={C.pro} />
                                <Bar dataKey="K12" stackId="t" fill={C.k12} radius={[0, 3, 3, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                        <p style={{ margin: 0, fontSize: '12px', color: 'var(--app-muted)' }}>
                            K12 → Pro moves are blocked by the tier lock, so this shifts mainly through new activations.
                        </p>
                    </Panel>
                </div>
                <div style={{ flex: '1 1 420px', minWidth: 0 }}>
                    <Panel title="API usage" hint="Activations made through reseller API keys"
                        right={<div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '20px', fontWeight: 800 }}>{num(data.apiKeys?.active)}</div>
                            <div style={{ fontSize: '12px', color: 'var(--app-muted)' }}>active keys · {num(data.apiKeys?.usedLast7Days)} used in 7 days</div>
                        </div>}>
                        <ResponsiveContainer width="100%" height={220}>
                            <ComposedChart data={view.s} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke={grid} vertical={false} />
                                <XAxis dataKey="label" stroke={axis} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
                                <YAxis stroke={axis} tick={{ fontSize: 11 }} tickFormatter={short} width={44} />
                                <Tooltip contentStyle={tipStyle} formatter={(v, n) => [num(v), n]} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="act_api" name="API activations" fill={C.api} radius={[3, 3, 0, 0]} />
                                <Line dataKey="api_credits" name="Credits spent" stroke={C.money} strokeWidth={2} dot={false} />
                            </ComposedChart>
                        </ResponsiveContainer>
                        <p style={{ margin: 0, fontSize: '12px', color: 'var(--app-muted)' }}>
                            {num(t.api)} API activations and {num(t.apiCredits)} credits in {days} days.
                        </p>
                    </Panel>
                </div>
            </div>

            {/* Teams */}
            <Panel title="Active Adobe teams" hint="Right now · busiest teams by active users"
                right={<div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    <span className="badge badge-success">{num(teams.withUsers)} in use</span>
                    <span className="badge badge-warning">{num(teams.inactive)} inactive</span>
                    <span className="badge badge-info">{num(teams.syncOff)} sync off</span>
                    <span className="badge badge-purple">{num(teams.disqualified)} not a target</span>
                    {teams.withError > 0 && <span className="badge badge-danger">{num(teams.withError)} with errors</span>}
                </div>}>
                <div style={{ overflowX: 'auto' }}>
                    <table className="data-table" style={{ minWidth: '560px' }}>
                        <thead><tr><th>Team</th><th>Type</th><th style={{ textAlign: 'right' }}>Active users</th><th>Adobe seats used</th><th>State</th></tr></thead>
                        <tbody>
                            {(data.topTeams || []).map(tm => {
                                const pctSeats = tm.total > 0 ? Math.min(100, Math.round(tm.used / tm.total * 100)) : 0;
                                return (
                                    <tr key={tm.name}>
                                        <td data-label="Team" style={{ fontWeight: 600, maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tm.name}</td>
                                        <td data-label="Type"><span className={`badge ${tm.k12 ? 'badge-info' : 'badge-warning'}`}>{tm.k12 ? 'K12' : 'Pro'}</span></td>
                                        <td data-label="Active users" style={{ textAlign: 'right', fontWeight: 700 }}>{num(tm.users)}</td>
                                        <td data-label="Seats">
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <div style={{ flex: '1 1 auto', height: '8px', background: 'var(--app-surface-muted)', borderRadius: '999px', overflow: 'hidden', minWidth: '60px' }}>
                                                    <div style={{ height: '100%', width: `${pctSeats}%`, background: pctSeats >= 95 ? C.bad : C.money, borderRadius: '999px' }} />
                                                </div>
                                                <span style={{ fontSize: '12px', color: 'var(--app-muted)', whiteSpace: 'nowrap' }}>{num(tm.used)}/{tm.total ? num(tm.total) : '∞'}</span>
                                            </div>
                                        </td>
                                        <td data-label="State" style={{ fontSize: '12px' }}>
                                            {tm.rateLimited ? <span style={{ color: C.bad, fontWeight: 700 }}>rate-limited</span>
                                                : tm.syncOff ? <span style={{ color: 'var(--app-muted)' }}>sync off</span>
                                                    : tm.inactive ? <span style={{ color: '#B45309' }}>inactive</span>
                                                        : <span style={{ color: C.money, fontWeight: 600 }}>normal</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Panel>

            {/* Team health */}
            <Panel title="Adobe team service health" hint="Outages and rate limits recorded per day">
                {t.incidents === 0 ? (
                    <div className="empty-state">
                        <p className="empty-text" style={{ margin: 0 }}>
                            No incidents recorded yet{data.incidentsSince ? '' : ' — recording started with this update'}.
                            Teams going unreachable and Adobe rate limits will appear here as they happen.
                        </p>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={view.s} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke={grid} vertical={false} />
                            <XAxis dataKey="label" stroke={axis} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
                            <YAxis stroke={axis} tick={{ fontSize: 11 }} allowDecimals={false} width={40} />
                            <Tooltip contentStyle={tipStyle} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="health_dead" name="Team unreachable" stackId="h" fill={C.bad} />
                            <Bar dataKey="health_ratelimit" name="Rate-limited" stackId="h" fill={C.pro} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
                {(data.recentIncidents || []).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>
                        {data.recentIncidents.slice(0, 6).map((i, k) => (
                            <div key={k} style={{ display: 'flex', gap: '10px', alignItems: 'baseline', borderTop: '1px solid var(--app-border)', paddingTop: '6px' }}>
                                <span style={{ color: 'var(--app-muted)', fontSize: '12px', whiteSpace: 'nowrap' }}>{new Date(i.at).toLocaleString()}</span>
                                <span className={`badge ${i.kind === 'recovered' ? 'badge-success' : i.kind === 'ratelimit' ? 'badge-warning' : 'badge-danger'}`}>{i.kind}</span>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.orgName || i.orgId}{i.detail ? ` · ${i.detail}` : ''}</span>
                            </div>
                        ))}
                    </div>
                )}
            </Panel>

            {/* Telegram bot */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '999 1 560px', minWidth: 0 }}>
                    <Panel title="Telegram bot performance" hint="Paid orders per day, with revenue">
                        <ResponsiveContainer width="100%" height={260}>
                            <ComposedChart data={view.s} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke={grid} vertical={false} />
                                <XAxis dataKey="label" stroke={axis} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
                                <YAxis yAxisId="l" stroke={axis} tick={{ fontSize: 11 }} allowDecimals={false} width={40} />
                                <YAxis yAxisId="r" orientation="right" stroke={axis} tick={{ fontSize: 11 }} tickFormatter={v => '$' + short(v)} width={56} />
                                <Tooltip contentStyle={tipStyle} formatter={(v, n) => [n === 'Revenue' ? usd2(v) : num(v), n]} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar yAxisId="l" dataKey="bot_orders" name="Orders" stackId="b" fill={C.money} />
                                <Bar yAxisId="l" dataKey="bot_refunded" name="Refunded" stackId="b" fill={C.grey} radius={[3, 3, 0, 0]} />
                                <Line yAxisId="r" dataKey="bot_usd" name="Revenue" stroke={C.pro} strokeWidth={2} dot={false} />
                            </ComposedChart>
                        </ResponsiveContainer>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', fontSize: '13px' }}>
                            <span><strong>{num(data.top?.botBuyers?.total)}</strong> <span style={{ color: 'var(--app-muted)' }}>people have used the bot</span></span>
                            <span><strong>{num(data.top?.botBuyers?.active7)}</strong> <span style={{ color: 'var(--app-muted)' }}>active in 7 days</span></span>
                            <span><strong>{num(t.topup)}</strong> <span style={{ color: 'var(--app-muted)' }}>credits topped up by agents</span></span>
                        </div>
                    </Panel>
                </div>
                <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Panel title="Bot profit" hint={`Last ${days} days · only where a supplier cost is known`}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ fontSize: '32px', fontWeight: 800, color: view.profit.margin >= 0 ? C.money : C.bad, lineHeight: 1.1 }}>
                                {usd2(view.profit.margin)}
                            </span>
                            <span style={{ fontSize: '13px', color: 'var(--app-muted)' }}>
                                {view.profit.pct}% margin · {num(view.profit.orders)} orders · {usd2(view.profit.revenue)} sold, {usd2(view.profit.cost)} cost
                            </span>
                        </div>
                        <div style={{ borderTop: '1px solid var(--app-border)', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ fontSize: '13px', color: 'var(--app-muted)' }}>All bot revenue</span>
                            <span style={{ fontSize: '20px', fontWeight: 800 }}>{usd2(t.botUsd)}</span>
                            <span style={{ fontSize: '12px', color: 'var(--app-muted)' }}>
                                Profit on the rest can't be worked out yet: no cost is stored for your own codes or Adobe seats.
                            </span>
                        </div>
                    </Panel>
                    <Panel title="Top bot products">
                        {(data.top?.bot || []).length === 0 ? <p style={{ margin: 0, fontSize: '13px', color: 'var(--app-muted)' }}>No sales in this period.</p> :
                            (data.top.bot).map(p => {
                                const max = Math.max(...data.top.bot.map(x => x.usd || 0), 1);
                                return (
                                    <div key={p.name} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '13px' }}>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                                            <strong style={{ whiteSpace: 'nowrap' }}>{usd2(p.usd)}</strong>
                                        </div>
                                        <div style={{ height: '6px', background: 'var(--app-surface-muted)', borderRadius: '999px', overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${Math.round((p.usd || 0) / max * 100)}%`, background: C.money, borderRadius: '999px' }} />
                                        </div>
                                        <span style={{ fontSize: '12px', color: 'var(--app-muted)' }}>{num(p.orders)} orders</span>
                                    </div>
                                );
                            })}
                    </Panel>
                </div>
            </div>

            {/* Deposits + marketplace */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: '1 1 440px', minWidth: 0 }}>
                    <Panel title="User deposits" hint="Money added to bot wallets"
                        right={<div style={{ textAlign: 'right' }}><div style={{ fontSize: '20px', fontWeight: 800 }}>{usd(t.dep)}</div><div style={{ fontSize: '12px', color: 'var(--app-muted)' }}>in {days} days</div></div>}>
                        <ResponsiveContainer width="100%" height={230}>
                            <AreaChart data={view.s} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke={grid} vertical={false} />
                                <XAxis dataKey="label" stroke={axis} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
                                <YAxis stroke={axis} tick={{ fontSize: 11 }} tickFormatter={v => '$' + short(v)} width={52} />
                                <Tooltip contentStyle={tipStyle} formatter={(v, n) => [usd2(v), n]} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Area type="monotone" dataKey="dep_binance" name="Binance Pay" stackId="d" stroke={C.money} fill={C.money} fillOpacity={0.7} />
                                <Area type="monotone" dataKey="dep_crypto" name="Crypto" stackId="d" stroke={C.k12} fill={C.k12} fillOpacity={0.7} />
                                <Area type="monotone" dataKey="dep_admin" name="Admin" stackId="d" stroke={C.grey} fill={C.grey} fillOpacity={0.7} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Panel>
                </div>
                <div style={{ flex: '1 1 440px', minWidth: 0 }}>
                    <Panel title="Marketplace performance" hint="Website orders, in gold coins"
                        right={<div style={{ textAlign: 'right' }}><div style={{ fontSize: '20px', fontWeight: 800 }}>{num(t.mktGc)} GC</div><div style={{ fontSize: '12px', color: 'var(--app-muted)' }}>{num(t.mktOrders)} orders</div></div>}>
                        <ResponsiveContainer width="100%" height={230}>
                            <ComposedChart data={view.s} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke={grid} vertical={false} />
                                <XAxis dataKey="label" stroke={axis} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
                                <YAxis yAxisId="l" stroke={axis} tick={{ fontSize: 11 }} tickFormatter={short} width={44} />
                                <YAxis yAxisId="r" orientation="right" stroke={axis} tick={{ fontSize: 11 }} tickFormatter={short} width={48} />
                                <Tooltip contentStyle={tipStyle} formatter={(v, n) => [num(v), n]} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar yAxisId="l" dataKey="mkt_orders" name="Orders" fill={C.alt} radius={[3, 3, 0, 0]} />
                                <Line yAxisId="r" dataKey="mkt_gc" name="Gold coins" stroke={C.pro} strokeWidth={2} dot={false} />
                            </ComposedChart>
                        </ResponsiveContainer>
                        <div style={{ fontSize: '12px', color: 'var(--app-muted)' }}>
                            {num(data.top?.marketplaceBuyers)} resellers bought · {num(t.mktRefunded)} orders refunded
                        </div>
                    </Panel>
                </div>
            </div>

            {/* Transfers */}
            <Panel title="User transfers" hint="Moves between teams and email changes"
                right={<div style={{ textAlign: 'right' }}><div style={{ fontSize: '20px', fontWeight: 800 }}>{num(t.trf)}</div><div style={{ fontSize: '12px', color: 'var(--app-muted)' }}>in {days} days</div></div>}>
                <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={view.s} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke={grid} vertical={false} />
                        <XAxis dataKey="label" stroke={axis} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
                        <YAxis stroke={axis} tick={{ fontSize: 11 }} tickFormatter={short} width={48} />
                        <Tooltip contentStyle={tipStyle} formatter={(v, n) => [num(v), n]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="trf_switch" name="User switched team" stackId="t" fill={C.k12} />
                        <Bar dataKey="trf_org" name="Reseller moved team" stackId="t" fill={C.pro} />
                        <Bar dataKey="trf_transfer" name="Email transfer" stackId="t" fill={C.api} />
                        <Bar dataKey="trf_email" name="User changed email" stackId="t" fill={C.grey} radius={[3, 3, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </Panel>

            <p style={{ margin: '0 0 8px', fontSize: '12px', color: 'var(--app-muted)', textAlign: 'center' }}>
                Daily totals are stored as they happen, so history keeps building even after old records are cleaned up.
                {data.queryMs != null && ` Loaded in ${data.queryMs} ms.`}
            </p>
        </div>
    );
}

export default AdminDashboard;
